import { useQuery } from '@tanstack/react-query';
import type { BookingBookBody, BookingCallbackBody, BookingCancelReason } from '@brimo/shared';
import { ApiError, api } from '../../../lib/api';

/**
 * Public booking page (docs/dev/api-booking.md): no session, no realtime. Queries are keyed under
 * `public-booking`; every write is a plain call so the page decides what happens with a 409.
 */

export interface PublicAdvisor {
  name: string;
  initials: string;
  roleLine: string | null;
}

export interface PublicOrganization {
  name: string;
  imprintUrl: string | null;
  privacyUrl: string | null;
}

export interface PublicPage {
  organization: PublicOrganization;
  eventType: {
    name: string;
    description: string | null;
    durationMinutes: number;
    durationLabel: string;
    channelLine: string;
  };
  advisor: PublicAdvisor | null;
  timezone: string;
  earliest: string;
  latest: string;
  reminderText: string | null;
  indexable: boolean;
  /** The type runs a customer sequence: setter fields „Gebucht durch (Setter)" + „Gesprächsnotiz" (D-375). */
  setterBooking: boolean;
}

export interface BookingDays {
  days: Array<{ date: string; available: boolean }>;
  nextAvailable: { date: string; label: string } | null;
}

export interface BookingTime {
  startsAt: string;
  label: string;
  taken: boolean;
}

export interface BookingTimes {
  dateLabel: string;
  times: BookingTime[];
}

export interface BookingHold {
  holdKey: string;
  startsAt: string;
  endsAt: string;
  expiresAt: string;
}

export interface BookingConfirmation {
  heading: string;
  text: string;
  email: string;
  reminderText: string | null;
}

export interface ManageView {
  state: 'booked' | 'cancelled' | 'past';
  appointment: {
    eventTypeName: string;
    startsAt: string;
    endsAt: string;
    durationMinutes: number;
    heading: string;
    shortLabel: string;
    inlineLabel: string;
    text: string;
    channelLine: string;
    email: string | null;
    reminderText: string | null;
    advisor: PublicAdvisor;
  };
  canReschedule: boolean;
  canCancel: boolean;
  booking: { orgSlug: string; typeSlug: string };
  organization: PublicOrganization;
  indexable: boolean;
}

/** `details` of a `409 SLOT_TAKEN`: the day's list with the lost time taken. */
export interface SlotTaken {
  message: string;
  startsAt: string | null;
  dateLabel: string | null;
  times: BookingTime[] | null;
}

const root = ['public-booking'] as const;

function pagePath(orgSlug: string, typeSlug: string): string {
  return `/public/booking/${encodeURIComponent(orgSlug)}/${encodeURIComponent(typeSlug)}`;
}

function tokenPath(token: string): string {
  return `/public/booking/t/${encodeURIComponent(token)}`;
}

/** The ICS download of „In Kalender eintragen". */
export function icsUrl(token: string): string {
  return `/api${tokenPath(token)}/ics`;
}

const noClientRetry = (count: number, error: unknown) =>
  !(error instanceof ApiError && error.status < 500) && count < 1;

export function usePublicPage(orgSlug: string, typeSlug: string, enabled = true) {
  return useQuery({
    queryKey: [...root, 'page', orgSlug, typeSlug],
    queryFn: ({ signal }) => api<PublicPage>(pagePath(orgSlug, typeSlug), { signal }),
    retry: noClientRetry,
    staleTime: 60_000,
    enabled,
  });
}

export function bookingDaysKey(orgSlug: string, typeSlug: string, month: string) {
  return [...root, 'days', orgSlug, typeSlug, month] as const;
}

export function useBookingDays(orgSlug: string, typeSlug: string, month: string | null) {
  return useQuery({
    queryKey: bookingDaysKey(orgSlug, typeSlug, month ?? 'none'),
    queryFn: ({ signal }) =>
      api<BookingDays>(`${pagePath(orgSlug, typeSlug)}/days?month=${month ?? ''}`, { signal }),
    enabled: month !== null,
    retry: noClientRetry,
    staleTime: 30_000,
  });
}

export function bookingTimesKey(orgSlug: string, typeSlug: string, date: string) {
  return [...root, 'times', orgSlug, typeSlug, date] as const;
}

export function useBookingTimes(orgSlug: string, typeSlug: string, date: string | null) {
  return useQuery({
    queryKey: bookingTimesKey(orgSlug, typeSlug, date ?? 'none'),
    queryFn: ({ signal }) =>
      api<BookingTimes>(`${pagePath(orgSlug, typeSlug)}/times?date=${date ?? ''}`, { signal }),
    enabled: date !== null,
    retry: noClientRetry,
    staleTime: 15_000,
  });
}

export function createHold(orgSlug: string, typeSlug: string, startsAt: string, holdKey: string | null) {
  return api<BookingHold>(`${pagePath(orgSlug, typeSlug)}/holds`, {
    method: 'POST',
    body: holdKey ? { startsAt, holdKey } : { startsAt },
  });
}

export function book(orgSlug: string, typeSlug: string, body: BookingBookBody) {
  return api<{ token: string; confirmation: BookingConfirmation }>(`${pagePath(orgSlug, typeSlug)}/book`, {
    method: 'POST',
    body,
  });
}

export function requestCallback(orgSlug: string, typeSlug: string, body: BookingCallbackBody) {
  return api<{ state: 'requested' }>(`${pagePath(orgSlug, typeSlug)}/callback`, { method: 'POST', body });
}

export function manageViewKey(token: string) {
  return [...root, 'token', token] as const;
}

export function useManageView(token: string) {
  return useQuery({
    queryKey: manageViewKey(token),
    queryFn: ({ signal }) => api<ManageView>(tokenPath(token), { signal }),
    retry: noClientRetry,
    staleTime: 10_000,
  });
}

export function reschedule(token: string, holdKey: string) {
  return api<{ confirmation: BookingConfirmation }>(`${tokenPath(token)}/reschedule`, {
    method: 'POST',
    body: { holdKey },
  });
}

export function cancelBooking(token: string, reason: BookingCancelReason | null) {
  return api<{ state: 'cancelled' }>(`${tokenPath(token)}/cancel`, {
    method: 'POST',
    body: reason ? { reason } : {},
  });
}

/** The `409 SLOT_TAKEN` of a hold, booking or reschedule, or null for any other error. */
export function slotTakenOf(error: unknown): SlotTaken | null {
  if (!(error instanceof ApiError) || error.status !== 409 || error.code !== 'SLOT_TAKEN') return null;
  const times = error.details?.['times'];
  return {
    message: error.message,
    startsAt: error.detailString('startsAt'),
    dateLabel: error.detailString('dateLabel'),
    times: Array.isArray(times) ? (times as BookingTime[]) : null,
  };
}

export function isNotFound(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

export function isGone(error: unknown): boolean {
  return error instanceof ApiError && error.status === 410;
}
